/* CalculatedFieldEditor.jsx – unified editor, no-cursor-jump version
   ----------------------------------------------------------------- */
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  Typography,
  Button,
  Divider,
} from '@mui/material';

import { createRoot } from 'react-dom/client';

import AddIcon        from '@mui/icons-material/Add';
import RemoveIcon     from '@mui/icons-material/Remove';
import FunctionsIcon  from '@mui/icons-material/Functions';

import FormFieldCustomSelectComponent from '../FormFieldCustomSelectComponent';
import ChipComponent                  from './ChipComponent';
import { FIELD_TYPE_CALCULATED }      from '../../Fields/constants/fieldTypes';
import { useFieldMetadata }           from '../../../contexts/FieldMetadataContext';   // 🆕

/* ――― constants ――― */
const DELIM_OPTS = [
  { label: 'space',      val: ' ' },
  { label: 'hyphen',     val: '-' },
  { label: 'underscore', val: '_' },
  { label: 'semicolon',  val: ';' },
  { label: 'colon',      val: ':' },
];
const iconSx = { fontSize: 14 };
const ICONS  = {
  ADD      : <AddIcon sx={iconSx}/>,
  SUB      : <RemoveIcon sx={iconSx}/>,
  CONCAT   : <Typography sx={{ fontSize:12, fontWeight:700, lineHeight:1 }}>C</Typography>,
  TEMPLATE : <Typography sx={{ fontSize:12, fontWeight:700, lineHeight:1 }}>T</Typography>,
};
const glyph = (v) => (v===' ' ? '␠' : v);
const OPT_SX = { fontSize:'0.75rem', minHeight:24, gap:1 };

/* ---------- helper – build chip span from UFI ---------- */
const makeChipSpan = (ufi, label) => {
  const span = document.createElement('span');
  span.dataset.ufi = ufi;
  span.contentEditable = 'false';

  const mount = document.createElement('span');
  span.appendChild(mount);
  createRoot(mount).render(<ChipComponent fieldUfi={ufi} label={label} />);

  return span;
};

/* ――― component ――― */
export default function CalculatedFieldEditor({
  meta = {}, onSave = () => {}, onClose = () => {},
}) {
  /* context – for label lookup when rebuilding chips */
  const { fieldsMetadata } = useFieldMetadata();

  /* draft state */
  const [label   , setLabel   ] = useState(meta.label || 'New calculated field');
  const [formula, setFormula] = useState(meta.formula   || 'TEMPLATE');
  const [delimiter, setDelimiter] = useState(meta.delimiter || ' ');
  const templateRef = useRef(meta.template || '');

  /* editable div ref */
  const editorRef = useRef(null);

  /* seed / rebuild editor when formula or incoming template changes */
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    if (formula !== 'TEMPLATE') {
      editor.innerHTML = '';               // leave blank for CONCAT/ADD/SUB
      return;
    }

    const tpl = templateRef.current || '';
    editor.innerHTML = '';                 // clear

    // split text and {{ufi}} tokens     
    tpl.split(/({{[\w-]+}})/g).forEach(part => {
      const m = part.match(/^{{([\w-]+)}}$/);
      if (m) {
        const ufi   = m[1];
        const label =
          Object.values(fieldsMetadata).find(f => f.ufi === ufi)?.label || ufi;
        editor.appendChild(makeChipSpan(ufi, label));
      } else if (part) {
        editor.appendChild(document.createTextNode(part));
      }
    });
  }, [formula, meta.template, fieldsMetadata]);

  /* ---------- chip insertion from picker ---------- */
  const insertChip = ({ value: ufi }) => {
    if (!ufi || !editorRef.current) return;

    const label =
      Object.values(fieldsMetadata).find(f => f.ufi === ufi)?.label || ufi;

    const chip = makeChipSpan(ufi, label);

    const sel = window.getSelection();
    if (sel && sel.rangeCount) {
      const range = sel.getRangeAt(0);
      range.deleteContents();
      range.insertNode(chip);
      // move caret after chip
      range.setStartAfter(chip);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      editorRef.current.appendChild(chip);
    }
    editorRef.current.focus();
  };

  /* ---------- save ---------- */
  const handleSave = () => {
    if (formula === 'TEMPLATE' && editorRef.current) {
      const clone = editorRef.current.cloneNode(true);
      clone.querySelectorAll('[data-ufi]').forEach(el => {
        const ufi = el.dataset.ufi;
        el.replaceWith(`{{${ufi}}}`);
      });
      templateRef.current = clone.textContent;   // store plain tokens
    }

    onSave({
      ...meta,
      label,
      type     : FIELD_TYPE_CALCULATED,
      formula,
      delimiter,
      template : templateRef.current,
    });
  };

  /* ---------- render ---------- */
  return (
    <Box sx={{ display:'flex', flexDirection:'column', height:'100%', gap:2 }}>
      {/* ── label editor ─────────────────────────────────────────────── */}
      <TextField
        variant="outlined"
        size="small"
        fullWidth
        label="Field label"
        value={label}
        onChange={e=>setLabel(e.target.value)}
        sx={{ mb:0.5 }}
      />

      {/* toolbar */}
      <Box sx={{
        display:'flex', alignItems:'center', gap:0.5,
        px:0.5, py:1, minHeight:44,
        bgcolor:'#f1f3f4', borderRadius:1, boxShadow:1,
        '& > *':{ flexShrink:0 },
      }}>
        {/* Σ = */}
        <Box sx={t=>({
          display:'flex', alignItems:'center',
          color:t.palette.text.disabled, mx:0.5, gap:0.25,
        })}>
          <FunctionsIcon sx={{ fontSize:20 }}/>
          <Typography sx={{ fontSize:'0.875rem', lineHeight:1 }}>=</Typography>
        </Box>

        {/* formula selector */}
        <Select
          value={formula}
          onChange={(e)=>setFormula(e.target.value)}
          variant="standard"
          disableUnderline
          size="small"
          renderValue={v=>(
            <Box sx={{ width:22, display:'flex', alignItems:'center', justifyContent:'center' }}>
              {ICONS[v]}
            </Box>
          )}
          MenuProps={{
            MenuListProps:{ dense:true },
            PaperProps:{ sx:{ '& .MuiMenuItem-root':OPT_SX }},
          }}
          sx={{
            minWidth:40, maxWidth:40, px:0,
            '& .MuiSelect-select':{ p:0, display:'flex', alignItems:'center', justifyContent:'center' },
            '& .MuiSelect-standard:focus':{ background:'transparent' },
            border:'none',
          }}
        >
          {Object.entries(ICONS).map(([v,icon])=>(
            <MenuItem key={v} value={v} sx={OPT_SX}>
              {icon}
              <Typography sx={{ ml:0.75, fontSize:12 }}>{v}</Typography>
            </MenuItem>
          ))}
        </Select>

        {/* field picker */}
        <FormFieldCustomSelectComponent
          dense
          sx={{ minWidth:140 }}
          value=""
          onChange={insertChip}
        />

        {/* delimiter */}
        <Select
          value={delimiter}
          onChange={(e)=>setDelimiter(e.target.value)}
          variant="standard"
          disableUnderline
          size="small"
          disabled={formula!=='CONCAT'}
          renderValue={v=>(
            <Box sx={{ width:22, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <Typography sx={{ fontSize:12, fontWeight:700 }}>{glyph(v)}</Typography>
            </Box>
          )}
          MenuProps={{
            MenuListProps:{ dense:true },
            PaperProps:{ sx:{ '& .MuiMenuItem-root':OPT_SX }},
          }}
          sx={{
            minWidth:40, maxWidth:40, px:0,
            '& .MuiSelect-select':{ p:0, display:'flex', alignItems:'center', justifyContent:'center' },
            '& .MuiSelect-standard:focus':{ background:'transparent' },
            border:'none',
            opacity: formula==='CONCAT' ? 1 : 0.4,
          }}
        >
          {DELIM_OPTS.map(({ label,val })=>(
            <MenuItem key={val} value={val} sx={OPT_SX}>
              <Typography sx={{ fontSize:12, fontWeight:700 }}>{glyph(val)}</Typography>
              <Typography sx={{ ml:0.75, fontSize:12 }}>{label}</Typography>
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* editable / preview area */}
      <Box
        ref={editorRef}
        contentEditable={formula==='TEMPLATE'}
        suppressContentEditableWarning
        style={{ whiteSpace:'pre-wrap', wordBreak:'break-word' }}
        sx={{
          flexGrow:1,
          p:1,
          border:'1px solid #ddd',
          borderRadius:1,
          minHeight:140,
          overflowY:'auto',
          fontSize:'0.875rem',
          lineHeight: 2,
          outline:'none',
          userSelect: formula==='TEMPLATE' ? 'text' : 'none',
          backgroundColor: formula==='TEMPLATE' ? '#fff' : '#fafafa',
          '&:empty:before':{
            content: '"Build your expression…"',   // MUST be quoted
            color  : 'rgba(0,0,0,0.38)',
          },
        }}
      />

      <Divider sx={{ my:1 }}/>

      <Box sx={{ display:'flex', justifyContent:'flex-end', gap:1 }}>
        <Button size="small" onClick={onClose}>Cancel</Button>
        <Button size="small" variant="contained" onClick={handleSave}>Save</Button>
      </Box>
    </Box>
  );
}
